import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  _get_anchor_banks,
  _get_beneficiaries,
  _bank_verification,
  _get_wallet_lookup_by_account_id,
  _complete_bank_transfer,
  _payout_internal,
  _verify_pin,
  _complete_business_transfer,
  _complete_business_wallet_transfer,
  _transfer_fourward,
} from "../common/axios_services";
import { updateCount } from "../slices/updateCountSlice";
import { showErrorNotification, showSuccessNotification } from "../common/utils/notification";


const useTransferModal = (onComplete, initialType = "bank", isBusiness = false) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Banks state
  const [allBanks, setAllBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);

  // Beneficiaries state
  const [userBen, setUserBen] = useState([]);
  const [loadingBen, setLoadingBen] = useState(false);

  // Transfer type
  const [transferType, setTransferType] = useState(initialType);

  // Form state
  const [selectedBank, setSelectedBank] = useState();
  const [accNo, setAccNo] = useState(0);
  const [loadingCommission, setLoadingCommission] = useState(false);
  const [normalCommission, setNormalCommission] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // Verification states
  const [bankBenDetails, setBankBenDetails] = useState(false);
  const [bankErrorMessage, setBankErrorMessage] = useState(false);
  const [bankAccountName, setBankAccountName] = useState("");
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);

  const [walletBenDetails, setWalletBenDetails] = useState(false);
  const [walletErrorMessage, setWalletErrorMessage] = useState(false);
  const [walletAccountName, setWalletAccountName] = useState("");
  const [loadingWalletDetails, setLoadingWalletDetails] = useState(false);

  // Transfer states
  const [payingBank, setPayingBank] = useState(false);
  const [payingWallet, setPayingWallet] = useState(false);
  const [transferErrorMessage, setTransferErrorMessage] = useState(false);

  // PIN states
  const [verifingPin, setVerifingPin] = useState(false);
  const [verifiedPin, setVerifiedPin] = useState(false);
  const [revealPinMessage, setRevealPinMessage] = useState(false);

  // Modal states
  const [payOut, setPayout] = useState(false);
  const [fundOptions, setFundOptions] = useState("wallet");
  const [transferInfo, setTransferInfo] = useState({});
  const [konnectTransferModal, setKonnectTransferModal] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [walletTransferTarget, setWalletTransferTarget] = useState("WALLET");

  // Fetch banks
  const fetchBanks = useCallback(async () => {
    try {
      const get_banks = await _get_anchor_banks();
      setAllBanks(get_banks?.data?.data?.data || []);
      setLoadingBanks(false);
    } catch (err) {
      setLoadingBanks(false);
      handleApiError(err);
    }
  }, [navigate]);

  // Fetch beneficiaries
  const fetchBeneficiaries = useCallback(async (type = transferType) => {
    setLoadingBen(true);
    try {
      const get_ben = await _get_beneficiaries({ type: type === "wallet" ? type : "" });
      setUserBen(get_ben?.data?.data?.beneficiaries || []);
    } catch (err) {
      setUserBen([]);
      handleApiError(err);
    } finally {
      setLoadingBen(false);
    }
  }, [transferType, navigate]);

  // Bank verification
  const verifyBankAccount = async (accNo, selectedBank) => {
    if (accNo && selectedBank) {
      setLoadingBankDetails(true);
      try {
        const banker = await _bank_verification({
          account_number: `${accNo}`,
          account_bank: `${selectedBank.title}`,
        });
        setLoadingBankDetails(false);
        if (banker.data.status === "error") {
          setBankBenDetails(false);
          setBankErrorMessage(banker.data.message);
          setBankAccountName("Not Found");
        } else {
          setBankErrorMessage(false);
          setBankBenDetails(banker.data);
          setBankAccountName(banker.data.data.accountName);
          return banker?.data?.data
        }
      } catch (err) {
        setBankBenDetails(false);
        setBankAccountName("");
        setLoadingBankDetails(false);
        handleApiError(err);
      }
    }
  };

  // Wallet verification
  const verifyWalletAccount = async (accountNo) => {
    setLoadingWalletDetails(true);
    try {
      const accOwner = await _get_wallet_lookup_by_account_id(accountNo);
      setLoadingWalletDetails(false);
      setWalletErrorMessage(false);
      setWalletBenDetails(accOwner?.data?.data);
      setWalletAccountName(accOwner?.data?.data?.userName);
      return accOwner?.data?.data;
    } catch (err) {
      setWalletBenDetails(false);
      setWalletAccountName("");
      setLoadingWalletDetails(false);
      handleApiError(err);
    }
  };

  // Transfer to bank
  const transferToBank = async (values, selectedBank, accNo) => {
    setPayingBank(true);
    try {
      const data = {
        bankCode: selectedBank.title,
        accountNumber: accNo,
        amount: +values.amount,
        accountName: bankAccountName,
        narration: `${values.narration}`,
      };
      const PayOut = isBusiness
        ? await _complete_business_transfer(data)
        : await _complete_bank_transfer(data);
      setPayingBank(false);
      setPayingWallet(false);
      showSuccessNotification(PayOut?.data?.message || "Transfer successful");
      dispatch(updateCount(Math.random()));
      if (onComplete) {
        onComplete();
      }
      return { success: true };
    } catch (err) {
      setPayingBank(false);
      handleApiError(err);
    }
  };

  // Transfer to wallet
  const transferToWallet = async (values, accNo) => {
    setPayingWallet(true);
    try {
      const data = {
        toAccountId: accNo,
        amount: +values.amount,
        note: `${values.naration}`,
      };
      const PayOut = walletTransferTarget === "FOURWARD"
        ? await _transfer_fourward(accNo, +values.amount)
        : isBusiness
          ? await _complete_business_wallet_transfer(data)
          : await _payout_internal(data);
      setPayingWallet(false);
      showSuccessNotification(PayOut?.data?.message || "Transfer successful");
      dispatch(updateCount(Math.random()));
      if (onComplete) {
        onComplete();
      }
      return { success: true };
    } catch (err) {
      setPayingWallet(false);
      handleApiError(err);
    }
  };

  // Verify PIN
  const verifyPin = async (pin) => {
    setVerifingPin(true);
    try {
      const verify_pin = await _verify_pin({ pin });
      setVerifingPin(false);
      showSuccessNotification(verify_pin?.data?.message || "PIN verified successfully");
      setRevealPinMessage(false);
      setVerifiedPin(true);
      return { success: true };
    } catch (err) {
      setVerifiedPin(false);
      setVerifingPin(false);
      handleApiError(err);
    }
  };

  // Reset functions
  const resetBankVerification = () => {
    setBankBenDetails(false);
    setBankErrorMessage(false);
    setBankAccountName("");
  };

  const resetWalletVerification = () => {
    setWalletBenDetails(false);
    setWalletErrorMessage(false);
    setWalletAccountName("");
  };

  const resetPinVerification = () => {
    setVerifiedPin(false);
    setRevealPinMessage(false);
  };

  const closePayout = () => {
    setPayout(false);
    setTransferErrorMessage(false);
    setTransferType("bank");
    setTransferTo("");
    setFundOptions("");
    resetBankVerification();
    resetWalletVerification();
    setNormalCommission(0);
    setTotalCommission(0);
    resetPinVerification();
    setSelectedBeneficiary(null);
    setWalletTransferTarget("WALLET");
  };

  // Initialize
  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  useEffect(() => {
    fetchBeneficiaries(transferType);
  }, [transferType, fetchBeneficiaries]);

  return {
    // Banks
    allBanks,
    loadingBanks,

    // Beneficiaries
    userBen,
    loadingBen,
    fetchBeneficiaries,

    // Transfer type
    transferType,
    setTransferType,

    // Form state
    selectedBank,
    setSelectedBank,
    accNo,
    setAccNo,
    loadingCommission,
    setLoadingCommission,
    normalCommission,
    setNormalCommission,
    totalCommission,
    setTotalCommission,
    selectedBeneficiary,
    setSelectedBeneficiary,

    // Bank verification
    bankBenDetails,
    setBankBenDetails,
    bankErrorMessage,
    bankAccountName,
    loadingBankDetails,
    verifyBankAccount,
    resetBankVerification,
    setBankAccountName,
    setBankErrorMessage,

    // Wallet verification
    walletBenDetails,
    setWalletBenDetails,
    walletErrorMessage,
    walletAccountName,
    loadingWalletDetails,
    verifyWalletAccount,
    resetWalletVerification,
    setWalletAccountName,
    setWalletErrorMessage,

    // Transfers
    payingBank,
    payingWallet,
    transferErrorMessage,
    transferToBank,
    transferToWallet,
    setTransferErrorMessage,

    // PIN
    verifingPin,
    verifiedPin,
    revealPinMessage,
    verifyPin,
    resetPinVerification,

    // Modal states
    payOut,
    setPayout,
    fundOptions,
    setFundOptions,
    transferInfo,
    setTransferInfo,
    konnectTransferModal,
    setKonnectTransferModal,
    transferTo,
    setTransferTo,
    walletTransferTarget,
    setWalletTransferTarget,

    // Actions
    closePayout,
  };
};

export default useTransferModal;

const handleApiError = (err) => {
  showErrorNotification(err?.response?.data?.message || err?.message || "Something went wrong");
};
